// src/pages/Volunteers.jsx
import React, { useState } from 'react';
import { 
  Box, Button, Typography, TextField, 
  Grid, Card, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Paper, 
  TablePagination, Avatar, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Checkbox,
  ListItemText, Switch, FormControlLabel
} from '@mui/material';
import { 
  Download as DownloadIcon, 
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

const volunteerColumns = [
  { id: 'id', label: 'ID', minWidth: 70 },
  { id: 'name', label: 'Volunteer', minWidth: 200 },
  { id: 'contact', label: 'Contact', minWidth: 150 },
  { id: 'interests', label: 'Areas of Interest', minWidth: 200 },
  { id: 'availability', label: 'Availability', minWidth: 150 },
  { id: 'assignedEvents', label: 'Assigned Events', minWidth: 150 },
  { id: 'status', label: 'Status', minWidth: 100 },
  { id: 'actions', label: 'Actions', minWidth: 150, align: 'center' },
];

const interestOptions = ['Food Distribution', 'Education', 'Healthcare', 'Animal Care', 'Environment', 'Fundraising'];
const availabilityOptions = ['Weekdays', 'Weekends', 'Mornings', 'Afternoons', 'Evenings', 'Flexible'];

const mockVolunteers = Array.from({ length: 30 }, (_, i) => ({
  id: i + 1,
  name: `Volunteer ${i + 1}`,
  avatar: `/avatars/${Math.floor(Math.random() * 5) + 1}.png`,
  email: `volunteer${i+1}@example.com`,
  phone: `+91 ${Math.floor(9000000000 + Math.random() * 1000000000)}`,
  interests: interestOptions.slice(0, Math.floor(Math.random() * 3) + 1),
  availability: availabilityOptions.slice(0, Math.floor(Math.random() * 2) + 1),
  assignedEvents: ['Food Drive', 'Education Camp', 'Tree Plantation'][Math.floor(Math.random() * 3)],
  status: Math.random() > 0.3 ? 'Active' : 'Inactive',
  notes: i % 4 === 0 ? 'Prefers weekend work' : '',
}));

const Volunteers = () => {
  const [volunteers, setVolunteers] = useState(mockVolunteers);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    interests: [],
    availability: [],
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [currentVolunteer, setCurrentVolunteer] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [volunteerToDelete, setVolunteerToDelete] = useState(null);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleInterestChange = (event) => {
    setFilters(prev => ({
      ...prev,
      interests: event.target.value,
    }));
  };

  const handleAvailabilityChange = (event) => {
    setFilters(prev => ({
      ...prev,
      availability: event.target.value,
    }));
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSearch = (e) => setSearchText(e.target.value);
  const handleResetFilters = () => {
    setFilters({
      status: '',
      interests: [],
      availability: [],
    });
    setSearchText('');
  };

  const handleExport = () => console.log('Export volunteers');
  
  const handleViewProfile = (volunteer) => {
    setCurrentVolunteer(volunteer);
    setOpenDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentVolunteer(null);
  };
  
  const handleToggleStatus = (id) => {
    setVolunteers(prev => prev.map(v => 
      v.id === id ? { ...v, status: v.status === 'Active' ? 'Inactive' : 'Active' } : v
    ));
  };
  
  const handleOpenDeleteDialog = (volunteer) => {
    setVolunteerToDelete(volunteer);
    setOpenDeleteDialog(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setVolunteerToDelete(null);
  };
  
  const handleDeleteVolunteer = () => {
    if (volunteerToDelete) {
      setVolunteers(prev => prev.filter(v => v.id !== volunteerToDelete.id));
      handleCloseDeleteDialog();
    }
  };
  
  const handleAddVolunteer = () => {
    setCurrentVolunteer({
      id: volunteers.length + 1,
      name: '',
      email: '',
      phone: '',
      interests: [],
      availability: [],
      assignedEvents: [],
      status: 'Active',
      notes: ''
    });
    setOpenDialog(true);
  };
  
  const handleSaveVolunteer = () => {
    if (currentVolunteer) {
      if (currentVolunteer.id > volunteers.length) {
        // New volunteer
        setVolunteers(prev => [...prev, currentVolunteer]);
      } else {
        // Update existing
        setVolunteers(prev => prev.map(v => v.id === currentVolunteer.id ? currentVolunteer : v));
      }
      handleCloseDialog();
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentVolunteer(prev => ({ ...prev, [name]: value }));
  };

  const filteredVolunteers = volunteers.filter(volunteer => {
    return (
      (filters.status ? volunteer.status === filters.status : true) &&
      (filters.interests.length > 0 
        ? filters.interests.every(interest => volunteer.interests.includes(interest)) 
        : true) &&
      (filters.availability.length > 0 
        ? filters.availability.every(avail => volunteer.availability.includes(avail)) 
        : true) &&
      (searchText 
        ? volunteer.name.toLowerCase().includes(searchText.toLowerCase()) || 
          volunteer.email.toLowerCase().includes(searchText.toLowerCase()) 
        : true)
    );
  });

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>Volunteers</Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Button 
          variant="contained" 
          startIcon={<PersonAddIcon />}
          onClick={handleAddVolunteer}
        >
          Add Volunteer
        </Button>
        <Box display="flex" gap={1}>
          <Button 
            variant="outlined" 
            startIcon={<FilterListIcon />}
            onClick={handleResetFilters}
          >
            Reset Filters
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export
          </Button>
        </Box>
      </Box>
      
      <Card sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search volunteers..."
              value={searchText}
              onChange={handleSearch}
              InputProps={{
                startAdornment: <SearchIcon color="action" />,
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Areas of Interest</InputLabel>
              <Select
                multiple
                name="interests"
                value={filters.interests}
                onChange={handleInterestChange}
                label="Areas of Interest"
                renderValue={(selected) => selected.join(', ')}
              >
                {interestOptions.map((interest) => (
                  <MenuItem key={interest} value={interest}>
                    <Checkbox checked={filters.interests.indexOf(interest) > -1} />
                    <ListItemText primary={interest} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Availability</InputLabel>
              <Select
                multiple
                name="availability"
                value={filters.availability}
                onChange={handleAvailabilityChange}
                label="Availability"
                renderValue={(selected) => selected.join(', ')}
              >
                {availabilityOptions.map((avail) => (
                  <MenuItem key={avail} value={avail}>
                    <Checkbox checked={filters.availability.indexOf(avail) > -1} />
                    <ListItemText primary={avail} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>
      
      <Card>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {volunteerColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    style={{ minWidth: column.minWidth, fontWeight: 600 }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredVolunteers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((volunteer) => (
                  <TableRow hover role="checkbox" tabIndex={-1} key={volunteer.id}>
                    <TableCell>{volunteer.id}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar src={volunteer.avatar} sx={{ width: 36, height: 36, mr: 2 }} />
                        {volunteer.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{volunteer.email}</Typography>
                        <Typography variant="body2" color="text.secondary">{volunteer.phone}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {volunteer.interests.map(interest => (
                          <Chip key={interest} label={interest} size="small" color="primary" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {volunteer.availability.map(avail => (
                          <Chip key={avail} label={avail} size="small" color="secondary" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {volunteer.assignedEvents.split(',').map(event => (
                          <Chip key={event} label={event} size="small" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={volunteer.status} 
                        size="small"
                        color={volunteer.status === 'Active' ? 'success' : 'error'}
                        sx={{ borderRadius: 1 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton 
                          size="small"
                          onClick={() => handleViewProfile(volunteer)}
                          color="primary"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={() => handleToggleStatus(volunteer.id)}
                          color={volunteer.status === 'Active' ? 'error' : 'success'}
                        >
                          {volunteer.status === 'Active' ? <CancelIcon /> : <CheckCircleIcon />}
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={() => handleOpenDeleteDialog(volunteer)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredVolunteers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* Volunteer Detail Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle>
          {currentVolunteer?.id > volunteers.length ? 'Add New Volunteer' : 'Volunteer Details'}
        </DialogTitle>
        <DialogContent dividers>
          {currentVolunteer && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={currentVolunteer.name}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={currentVolunteer.email}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={currentVolunteer.phone}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={currentVolunteer.status}
                    onChange={handleInputChange}
                    label="Status"
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Areas of Interest</InputLabel>
                  <Select
                    multiple
                    name="interests"
                    value={currentVolunteer.interests}
                    onChange={(e) => setCurrentVolunteer({...currentVolunteer, interests: e.target.value})}
                    label="Areas of Interest"
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {interestOptions.map((interest) => (
                      <MenuItem key={interest} value={interest}>
                        <Checkbox checked={currentVolunteer.interests.indexOf(interest) > -1} />
                        <ListItemText primary={interest} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Availability</InputLabel>
                  <Select
                    multiple
                    name="availability"
                    value={currentVolunteer.availability}
                    onChange={(e) => setCurrentVolunteer({...currentVolunteer, availability: e.target.value})}
                    label="Availability"
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {availabilityOptions.map((avail) => (
                      <MenuItem key={avail} value={avail}>
                        <Checkbox checked={currentVolunteer.availability.indexOf(avail) > -1} />
                        <ListItemText primary={avail} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Assigned Events"
                  name="assignedEvents"
                  value={currentVolunteer.assignedEvents}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                  helperText="Separate multiple events with commas"
                />
                <TextField
                  fullWidth
                  label="Notes/Feedback"
                  name="notes"
                  value={currentVolunteer.notes}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSaveVolunteer} color="primary" variant="contained">
            Save Volunteer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete volunteer {volunteerToDelete?.name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteVolunteer} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Volunteers;